import type { SubmissionResult } from '@conform-to/react';
import { z } from 'zod';

import { OTP_DIGIT_COUNT } from '~/common/otp';

export const loginSchema = (recaptchaEnabled: boolean) =>
  z.object({
    email: z
      .string({ error: 'メールアドレスは必須です' })
      .email('メールアドレスが不正です'),
    password: z
      .string({ error: 'パスワードは必須です' })
      .min(6, 'パスワードが短すぎます（6文字以上）')
      .max(20, 'パスワードが長すぎます（20文字以内）'),
    intent: z.string({ error: 'intentが存在しません' }),
    ...(recaptchaEnabled ? { recaptcha_token: z.string() } : {}),
  });

export const otpInputSchema = z.object({
  otp: z
    .string()
    .length(
      OTP_DIGIT_COUNT,
      `ワンタイムパスワードは${OTP_DIGIT_COUNT}桁である必要があります`,
    )
    .regex(
      new RegExp(`^\\d{${OTP_DIGIT_COUNT}}$`),
      '入力は半角数字である必要があります',
    ),
});

export const generateAdminPasswordResetSchema = z.object({
  adminId: z.string().guid(),
});

export const deleteAdminSchema = z.object({
  adminId: z.string().guid(),
});

export const createAdminUserSchema = z.object({
  email: z
    .string({ error: 'メールアドレスを入力してください' })
    .email('有効なメールアドレスを入力してください'),
});

export const resetAdminPasswordSchema = z.object({
  password: z
    .string({ error: 'パスワードを入力してください' })
    .min(8, 'パスワードは8文字以上で入力してください')
    .max(20, 'パスワードが長すぎます（20文字以内）')
    .regex(/^(?=.*[A-Za-z])[\x21-\x7E]{8,}$/, {
      message: 'パスワードは半角英数字・記号を含む8文字以上で入力してください',
    }),
});

/**
 * Admin ログインアクション（loginAction）が返す結果の公開コントラクト。
 * front（AdminLoginForm）はこの型のみに依存し、server の action 実装型（typeof）を参照しない。
 *
 * 他の admin_context 系アクションと違い **notification を持たない**。ログイン失敗は toast では
 * なく Conform のフォームエラー（`parseResult` の formErrors）として画面に出す作りのため。
 * 認証成功時は redirect するので fetcher.data には現れない（契約の対象は失敗分岐のみ）。
 *
 * parseResult は Conform の SubmissionResult を useForm の lastResult へ渡すだけの
 * 不透明ペイロードのため z.custom で型のみ保持する（ランタイム検証対象は ok）。
 */
export const adminLoginActionResultSchema = z.object({
  ok: z.boolean(),
  parseResult: z.custom<SubmissionResult<string[]>>(),
});

export type AdminLoginActionResult = z.infer<
  typeof adminLoginActionResultSchema
>;
